import { BACKEND_URL } from '../../../../constants';

type QueryValue
  = | string
  | number
  | boolean
  | null
  | QueryValue[]
  | { [key: string]: QueryValue };

type QueryParams = Record<string, QueryValue>;

const buildQueryString = (params: QueryParams, parentKey = 'filter'): string => {
  return Object.entries(params)
    .map(([key, value]) => {
      const fullKey = parentKey ? `${parentKey}[${key}]` : key;

      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        return buildQueryString(value, fullKey);
      }

      // mimic old behavior: skip falsy values
      const isFalsy
        = value === undefined
        || value === null
        || value === false
        || value === 0
        || value === '';

      if (isFalsy) return '';

      // keep key not encoded to preserve original URL format
      return `${fullKey}=${encodeURIComponent(String(value))}`;
    })
    .filter(Boolean)
    .join('&');
};

export const createUrlWithNestedFilter = (
  url: string,
  filter?: object,
  sort?: string,
  perPage?: number,
  page?: number,
  include?: string,
  customParam?: object
) => {
  const params: string[] = [];

  if (filter && Object.keys(filter).length > 0) {
    const filterQuery = buildQueryString(filter as QueryParams);
    if (filterQuery) params.push(filterQuery);
  }

  if (sort) params.push(`sort=${sort}`);
  if (perPage) params.push(`per_page=${perPage}`);
  if (page) params.push(`page=${page}`);
  if (include) params.push(`include=${include}`);

  if (customParam) {
    for (const [key, val] of Object.entries(customParam)) {
      if (val) params.push(`${key}=${val}`);
    }
  }

  return `${BACKEND_URL}${url}?${params.join('&')}`;
};

export const createUrl = (
  url: string,
  filter?: object,
  sort?: string,
  perPage?: number,
  page?: number,
  include?: string,
  customParam?: object,
  arrayParams?: { [key: string]: Array<string | number> },
  includeBackendUrl: boolean = true
): string => {
  const queryParts: string[] = [];

  if (filter && Object.keys(filter).length > 0) {
    const filterQuery = buildQueryString(filter as QueryParams);
    if (filterQuery) queryParts.push(filterQuery);
  }

  if (sort) queryParts.push(`sort=${sort}`);
  if (perPage) queryParts.push(`per_page=${perPage}`);
  if (page) queryParts.push(`page=${page}`);
  if (include) queryParts.push(`include=${include}`);

  if (customParam) {
    for (const [key, val] of Object.entries(customParam)) {
      if (val) queryParts.push(`${key}=${val}`);
    }
  }

  if (arrayParams) {
    for (const [key, values] of Object.entries(arrayParams)) {
      values.forEach(value => {
        queryParts.push(`${key}[]=${value}`);
      });
    }
  }

  return `${includeBackendUrl ? BACKEND_URL : ''}${url}?${queryParts.join('&')}`;
};